import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import { compact } from 'lodash-es'
import type { FC, ReactNode, RefObject } from 'react'
import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'react-toastify'
import { useAsyncFn } from 'react-use'
import { match, P } from 'ts-pattern'
import FluentAdd20Regular from '~icons/fluent/add-20-regular'
import MingcuteMore2Fill from '~icons/mingcute/more-2-fill'
import ModelCard from 'src/components/GenerationModel/ModelCard'
import { UIMenu, UIMenuItem, UIMenuTrigger } from 'src/components/ui/Menu'
import {
  UIDialog,
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import { useEditItems } from 'src/components/UserProfile/EditItems'
import { executeDeleteArtwork } from 'src/services/apollo/artwork'
import {
  executeUpdateBookmarkItem,
  useListBookmarkItems,
} from 'src/services/apollo/bookmark'
import type {
  Artwork,
  ArtworkPreviewFragment,
  Bookmark,
  BookmarkItemBaseFragment,
  GenerationModel,
} from 'src/services/apollo/generated/graphql'
import {
  ArtworkType,
  ArtworkVisibilityType,
  RefType,
} from 'src/services/apollo/generated/graphql'
import {
  addCollectionItem,
  removeCollectionItem,
  updateCollection,
} from 'src/services/helpers/bookmarkCollection'
import {
  OrderOption,
  type OrderOptionType,
} from 'src/services/helpers/orderOptionParams'
import { useShowNsfw } from 'src/services/helpers/preference'
import {
  type PersonalArtworkSort,
  useListUserArtworks,
} from 'src/services/orpc/userArtworks'
import { usePersonalPageActiveArtwork } from 'src/stores/artwork-modal'
import { getArtworkLink, isAnimatedArtwork } from 'src/utils/artwork'
import { getContentSafetyLabelForUserContent } from 'src/utils/contentSafety'
import { type DownloadState, downloadArtworks } from 'src/utils/download'
import { useQs } from 'src/utils/qs'
import { useNavigation } from 'src/utils/router/route'
import { useHash } from 'src/utils/router/useHash'
import { track } from 'src/utils/tracker'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { dialogModerationEdit } from '../Admin/Moderation/ModerationEditDialog'
import ArtworkPreviewCardSimple from '../Artwork/ArtworkPreviewCardSimple'
import ArtworkDetailDialog from '../ArtworkDetail/ArtworkDetailDialog'
import type {
  BaseCursorPagingContextType,
  BaseCursorPagingRef,
} from '../CursorPaging/BaseCursorPaging'
import BaseCursorPaging from '../CursorPaging/BaseCursorPaging'
import type { OnCursorChange } from '../CursorPaging/CursorPagingButton'
import Empty from '../Empty'
import UserModelList from '../GenerationModel/UserModelList'
import { Preloader } from '../Preloader/Preloader'
import { UIButton } from '../ui/Button'
import { useUserProfileContext } from '../UserProfile/UserProfile.context'
import type { CollectionListRef } from '../UserProfile/WorkCollection'
import { AddToCollectionButton } from '../UserProfile/WorkCollection'
import type { WorkTypes } from '../UserProfile/WorkTypeTabs'
import WorkTypeTabs from '../UserProfile/WorkTypeTabs'

const FILTER_KEYS = ['sort', 'visibility', 'after', 'before'] as const

export default function PersonalArtworkList(props: {
  userId: string
  username?: string
  showFilters?: boolean
  isMe?: boolean
  isAdmin?: boolean
  type?: WorkTypes | ''
  collectionListRef: RefObject<CollectionListRef | null>
}) {
  const { collectionId } = useParams()

  return collectionId ? (
    <PersonalArtworkListForBookmark
      isMe={props.isMe}
      bookmarkId={collectionId}
      userId={props.userId}
      username={props.username}
      type={props.type}
      collectionListRef={props.collectionListRef}
    />
  ) : (
    <PersonalArtworkListForAll {...props} />
  )
}

const PersonalArtworkListForAll: FC<{
  userId?: string
  username?: string
  showFilters?: boolean
  isMe?: boolean
  isAdmin?: boolean
  type?: WorkTypes | ''
}> = ({
  userId,
  username,
  showFilters = true,
  isMe = false,
  isAdmin = false,
  type,
}) => {
  const { t } = useTranslation(['artwork', 'common', 'user', 'action'])
  const navigation = useNavigation()
  const [
    {
      after: queryAfter,
      before: queryBefore,
      visibility: queryVisibility,
      sort: querySort,
    },
  ] = useQs()
  const [hash, setHash] = useHash()
  const hashParams = useMemo(
    () => new URLSearchParams(hash.replace(/^#/, '')),
    [hash],
  )

  const queryValues = useMemo(
    () => ({
      sort: querySort,
      visibility: queryVisibility,
      after: queryAfter,
      before: queryBefore,
    }),
    [querySort, queryVisibility, queryAfter, queryBefore],
  )

  // Migrate from query params to hash params (one-time migration)
  // This effect only runs when query params exist, moving them to hash
  // while preserving any other existing hash params
  useEffect(() => {
    const hasQueryParams = FILTER_KEYS.some(key => queryValues[key])
    if (!hasQueryParams) return

    const url = new URL(window.location.href)
    for (const key of FILTER_KEYS) {
      url.searchParams.delete(key)
    }

    const params = new URLSearchParams(url.hash.replace(/^#/, ''))
    for (const key of FILTER_KEYS) {
      const value = queryValues[key]
      if (value) {
        params.set(key, value)
      } else {
        params.delete(key)
      }
    }

    url.hash = params.toString()
    // Use replaceState instead of navigate() to avoid intermediate state where both query and hash params exist
    window.history.replaceState(null, '', url.toString())

    setHash(params.toString())
  }, [queryValues, setHash])

  const sort = hashParams.get('sort') || undefined
  const visibility = hashParams.get('visibility') || undefined
  const after = hashParams.get('after') || undefined
  const before = hashParams.get('before') || undefined
  const [activeArtwork, setActiveArtwork] = usePersonalPageActiveArtwork()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setActiveArtwork(undefined), [navigation])

  const pagingRef = useRef<BaseCursorPagingRef>(null)

  const onFilterChange = (args: Record<string, any>) => {
    const params = new URLSearchParams(hash.replace(/^#/, ''))
    // Clear pagination params on filter change so a new sort starts from
    // page 1 instead of a stale cursor that may not exist in the new sort.
    params.delete('before')
    params.delete('after')
    if (args.sort) {
      params.set('sort', args.sort)
    } else {
      params.delete('sort')
    }
    if (args.visibility) {
      params.set('visibility', args.visibility)
    } else {
      params.delete('visibility')
    }
    setHash(params.toString())
  }

  const onCursorChange: OnCursorChange = ({
    after: newAfter,
    before: newBefore,
  }) => {
    const params = new URLSearchParams(hash.replace(/^#/, ''))
    if (newAfter) {
      params.set('after', newAfter)
      params.delete('before')
    } else if (newBefore) {
      params.set('before', newBefore)
      params.delete('after')
    } else {
      params.delete('after')
      params.delete('before')
    }
    setHash(params.toString())
  }

  const visibilityOptions = [
    ['all', t('common:visibility.all')],
    ['private', t('common:visibility.private')],
    ['public', t('common:visibility.public')],
  ]

  const orderOptions = [
    [OrderOption.newest, t('common:order-option.newest')],
    [OrderOption.oldest, t('common:order-option.oldest')],
    [OrderOption.mostLiked, t('common:order-option.most-liked')],
  ]

  const visibilityFilter = (
    <FormControl
      className="flex-1 min-w-[140px] sm:flex-initial sm:w-[200px]"
      size="small"
    >
      <InputLabel>{t('common:visibility.label')}</InputLabel>
      <Select
        label={t('common:visibility.label')}
        value={visibility || visibilityOptions[0][0]}
        onChange={e => {
          onFilterChange({
            visibility: e.target.value,
          })
        }}
      >
        {visibilityOptions.map(([value, label]) => {
          return (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
  const orderOption = (
    <FormControl
      className="flex-1 min-w-[140px] sm:flex-initial sm:w-[200px]"
      size="small"
    >
      <InputLabel>{t('common:order-option.sort')}</InputLabel>
      <Select
        label={t('common:order-option.sort')}
        value={sort || orderOptions[0][0]}
        onChange={e => {
          onFilterChange({
            sort: e.target.value,
          })
        }}
      >
        {orderOptions.map(([value, label]) => {
          return (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )

  const editState = useEditItems({
    actionButtonName: t('action:manage'),
    deleteItem: id => executeDeleteArtwork({ id }),
    afterDelete: () => {
      pagingRef.current?.mutate()
    },
    dialogTitle: t('artwork:delete-artwork.confirm.title'),
    dialogContent: t('artwork:delete-artwork.confirm.content'),
  })
  const selectedArtworkCache = useRef(new Map<string, ArtworkPreviewFragment>())

  const tapArtwork = (artwork: ArtworkPreviewFragment) => {
    if (editState.editing) {
      if (editState.selectedItems.includes(artwork.id)) {
        selectedArtworkCache.current.delete(artwork.id)
      } else {
        selectedArtworkCache.current.set(artwork.id, artwork)
      }
      editState.toggleSelected(artwork.id)
    } else {
      setActiveArtwork(artwork)
    }
  }

  // add or remove artwork to bookmark
  const [loadingSetBookmarkItems, setBookmarkItems] = useLoadingCallback(
    async (bookmark: Bookmark, isDelete: boolean) => {
      if (editState.selectedItems.length === 0) return
      try {
        await Promise.all(
          editState.selectedItems.map(artworkId =>
            isDelete
              ? removeCollectionItem(bookmark.id, { artworkId })
              : addCollectionItem(bookmark.id, { artworkId }),
          ),
        )
        editState.cancelSelected()
        toast.success(t('user:collection.notice.add.success'))
      } catch (e: any) {
        toast.error(t('user:collection.notice.add.failed'))
      }
    },
    [editState],
  )

  const [downloadState, setDownloadState] = useState<DownloadState>({
    progress: 0,
    total: 0,
  })
  const [{ loading: downloading }, downloadSelected] = useAsyncFn(async () => {
    try {
      await downloadArtworks(
        compact(
          editState.selectedItems.map(id =>
            selectedArtworkCache.current.get(id),
          ),
        ),
        setDownloadState,
      )
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : String(err))
      setDownloadState({ progress: 0, total: 0 })
    }
  }, [editState])

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex flex-col gap-6">
        {isMe || isAdmin ? (
          <div className="flex gap-3 flex-wrap items-center">
            {showFilters && (
              <div className="flex gap-3 flex-1 min-w-0">
                {visibilityFilter}
                {orderOption}
              </div>
            )}
            <div className="flex gap-3 flex-wrap items-center sm:ml-auto">
              {editState.editItemsAction}
              {editState.editing && (
                <>
                  <AddToCollectionButton
                    userId={userId}
                    loading={loadingSetBookmarkItems}
                    selectedItems={editState.selectedItems}
                    addItemsToCollection={bookmark =>
                      setBookmarkItems(bookmark, false)
                    }
                  />
                  <UIButton
                    size="large"
                    variant="filled"
                    busy={downloading && downloadState.progress === 0}
                    disabled={
                      editState.selectedItems.length === 0 || downloading
                    }
                    className="capitalize"
                    onPress={downloadSelected}
                  >
                    {downloading ? (
                      `${downloadState.progress} / ${downloadState.total}`
                    ) : (
                      <Text i18nKey="action:download" />
                    )}
                  </UIButton>
                </>
              )}
              {isAdmin && editState.editing && (
                <Button
                  color="error"
                  variant="outlined"
                  disabled={editState.selectedItems.length === 0}
                  onClick={() => {
                    dialogModerationEdit({
                      refIds: editState.selectedItems,
                      refType: RefType.Artwork,
                      mutate: () => {
                        pagingRef.current?.mutate()
                      },
                    })
                  }}
                >
                  Batch Moderating
                </Button>
              )}
            </div>
          </div>
        ) : (
          showFilters && (
            <div className="flex gap-4 flex-wrap">{orderOption}</div>
          )
        )}
      </div>

      <AllArtworksList
        userId={userId}
        isMe={isMe}
        types={match(type)
          .with('', 'artwork', () => [ArtworkType.Default, ArtworkType.Album])
          // .with('manga', () => [ArtworkType.Manga]) @deprecated at 2025-07-23
          .with('animation', () => [ArtworkType.AnimatedArtwork])
          .otherwise(() => undefined)}
        editing={editState.editing}
        selectedItems={editState.selectedItems}
        pagingRef={pagingRef}
        tapArtwork={tapArtwork}
        cursorState={{ before, after }}
        filterState={{ visibility, sort }}
        onCursorChange={onCursorChange}
        showContentSafetyLabel
      />
      <ArtworkDetailDialog
        artwork={activeArtwork}
        open={!!activeArtwork}
        onClose={() => {
          setActiveArtwork(undefined)
          pagingRef.current?.mutate()
        }}
      />
    </div>
  )
}

const PersonalArtworkListForBookmark: FC<{
  bookmarkId: string
  userId: string
  isMe?: boolean
  username?: string
  type?: WorkTypes | ''
  collectionListRef: RefObject<CollectionListRef | null>
}> = props => {
  const { t } = useTranslation(['artwork', 'common', 'user'])
  const [showNsfw] = useShowNsfw()
  const [activeArtwork, setActiveArtwork] = usePersonalPageActiveArtwork()
  const pagingRef = useRef<BaseCursorPagingRef>(null)

  const { isMyProfile } = useUserProfileContext()
  const [openPickArtworksDialog, setOpenPickArtworksDialog] = useState(false)

  // A cache to store the types of selected items since `useEditItems`
  // doesn't store any info other than IDs.
  // It's not supposed to trigger rerender, so a ref is enough.
  // TODO: This is a kind of hack or workaround.
  //       A proper store implementation (like Context) would be better.
  const selectedItemTypeMapRef = useRef(new Map<string, 'artwork' | 'model'>())

  const deleteItems = (id: string, bookmarkId: string) => {
    return executeUpdateBookmarkItem({
      input: {
        bookmarkId: bookmarkId,
        ...(selectedItemTypeMapRef.current.get(id) === 'model'
          ? { modelId: id }
          : { artworkId: id }),
        remove: true,
      },
    })
  }

  const editBookmarkState = useEditItems({
    actionButtonName: t('user:collection.manage.title'),
    primaryButtonName: t('user:collection.manage.remove')!,
    primaryButtonAfterClick: () => {
      track('prinmaryButtonEvent')
    },
    beforeDelete: () => {
      track('collection_image_remove_confirm', {
        imageCount: editBookmarkState.selectedItems.length,
        collectionId: editBookmarkState.selectedItems,
      })
    },
    deleteItem: id => deleteItems(id, props.bookmarkId),
    afterDelete: () => {
      pagingRef.current?.mutate()
    },
    dialogTitle: t('user:collection.delete.confirm.title'),
    dialogContent: t('user:collection.delete.confirm.content'),
  })

  editBookmarkState.deleteItemAction = id => deleteItems(id, props.bookmarkId)

  // add or remove artwork to bookmark
  const [loadingSetBookmarkItems, setBookmarkItems] = useLoadingCallback(
    async (selectedItems: Array<{ id: string; type: string }>) => {
      try {
        if (selectedItems.length === 0) return
        await Promise.all(
          selectedItems.map(it => {
            return addCollectionItem(
              props.bookmarkId,
              it.type === 'model' ? { modelId: it.id } : { artworkId: it.id },
            )
          }),
        )
        pagingRef.current?.mutate()
        toast.success(t('user:collection.notice.add.success'))
        setOpenPickArtworksDialog(false)
      } catch {
        toast.error(t('user:collection.notice.add.failed'))
      }
    },
    [pagingRef, props.bookmarkId],
  )

  function tapArtwork(artwork: ArtworkPreviewFragment) {
    selectedItemTypeMapRef.current.set(artwork.id, 'artwork')
    if (editBookmarkState.editing) {
      editBookmarkState.toggleSelected(artwork.id)
    } else {
      setActiveArtwork(artwork)
    }
  }

  function tapModel(model: GenerationModel) {
    selectedItemTypeMapRef.current.set(model.id, 'model')
    if (editBookmarkState.editing) {
      editBookmarkState.toggleSelected(model.id)
    }
  }

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex flex-col  gap-6">
        {isMyProfile && (
          <div className="flex gap-4 flex-wrap h-10">
            <div className="flex-1"></div>
            {editBookmarkState.editItemsAction}

            <Button
              variant="contained"
              startIcon={<FluentAdd20Regular className="size-5" />}
              className="px-3 rounded-lg h-10 py-0"
              onClick={() => {
                setOpenPickArtworksDialog(true)
                track('collection_image_add_click')
              }}
            >
              <p className="text-sm font-medium text-center">
                {t('user:collection.action.add')}
              </p>
            </Button>
          </div>
        )}
      </div>

      <BookmarkedWorkList
        isMe={props.isMe ?? false}
        bookmarkId={props.bookmarkId}
        type={props.type}
        editing={editBookmarkState.editing}
        selectedItems={editBookmarkState.selectedItems}
        showNsfw={showNsfw}
        pagingRef={pagingRef}
        tapArtwork={tapArtwork}
        tapModel={tapModel}
        showContentSafetyLabel={isMyProfile}
        collectionListRef={props.collectionListRef}
      />

      <ArtworkDetailDialog
        artwork={activeArtwork}
        open={!!activeArtwork}
        onClose={() => setActiveArtwork(undefined)}
      />

      <PickItemDialog
        userId={props.userId}
        open={openPickArtworksDialog}
        loading={loadingSetBookmarkItems}
        confirmSelected={setBookmarkItems}
        onClose={() => {
          setOpenPickArtworksDialog(false)
        }}
      />
    </div>
  )
}

// Map URL-hash sort (`OrderOption.*` values) to the oRPC endpoint key.
// Computed keys (rather than string literals) keep this in sync with
// `OrderOption` if its underlying values are ever renamed.
const SORT_TO_PERSONAL = {
  [OrderOption.newest]: 'latest',
  [OrderOption.oldest]: 'oldest',
  [OrderOption.mostLiked]: 'mostLiked',
} as const satisfies Partial<Record<OrderOptionType, PersonalArtworkSort>>

const AllArtworksList: FC<{
  userId?: string
  isMe: boolean
  types?: ArtworkType[]
  editing: boolean
  selectedItems: string[]
  pagingRef: RefObject<BaseCursorPagingContextType | null> | undefined
  tapArtwork: (artwork: ArtworkPreviewFragment) => void
  cursorState: {
    before?: string
    after?: string
  }
  filterState?: {
    visibility?: string | null
    sort?: string | null
  }
  onCursorChange: (cursor: { before?: string; after?: string }) => any
  showContentSafetyLabel?: boolean
}> = ({
  userId,
  isMe,
  types,
  editing,
  selectedItems,
  pagingRef,
  tapArtwork,
  filterState,
  cursorState,
  onCursorChange,
  showContentSafetyLabel,
}) => {
  const { t } = useTranslation(['artwork'])
  const { visibility, sort } = filterState ?? {}
  const { before, after } = cursorState

  return (
    // Previously wrapped in `<Suspense fallback={<Preloader />}>`.
    // The grid render path has no suspending hooks (`useSuspense:
    // false` for i18next, react-query `useQuery`, no `React.lazy`),
    // so the boundary caught nothing but kept SSR HTML pinned in a
    // `<div hidden id="S:N">` swap target the JS-disabled client
    // never reveals. Removing it lets `<BaseCursorPaging>`'s grid
    // (real `<img>`s from the loader-seeded react-query cache)
    // inline directly into `<main>`.
    <BaseCursorPaging
      pagingRef={pagingRef}
      useSWR={useListUserArtworks}
      getConnection={r => r}
      pageSize={20}
      renderItem={(artwork: ArtworkPreviewFragment) => (
        <ArtworkPreviewCardSimple
          isEdit={editing}
          selected={selectedItems.includes(artwork.id)}
          key={artwork.id}
          artwork={artwork}
          to={getArtworkLink(artwork)}
          onClick={ev => ev.preventDefault()}
          onArtworkClick={tapArtwork as never}
          hideShare={isAnimatedArtwork(artwork)}
          contentSafetyLabelType={
            showContentSafetyLabel
              ? getContentSafetyLabelForUserContent(artwork)
              : undefined
          }
          trackingArgs={{
            scene: isMe ? 'profile-works' : 'other-works',
            section: types
              ? types.includes(ArtworkType.AnimatedArtwork)
                ? 'animations'
                : 'artworks'
              : undefined,
            feed: sort || undefined,
          }}
        />
      )}
      empty={<Empty description={t('artwork:no-content')} />}
      extraArgs={{
        userId,
        sort: SORT_TO_PERSONAL[sort ?? ''] ?? 'latest',
        visibility:
          visibility === 'private'
            ? ArtworkVisibilityType.Private
            : visibility === 'public'
              ? ArtworkVisibilityType.Public
              : undefined,
        type: types?.includes(ArtworkType.AnimatedArtwork)
          ? 'animation'
          : 'artwork',
        // `isNsfw` was a no-op in the GraphQL `artworks` resolver
        // (destructured as `_notUsed` in `resolveArtworksFromDB`, never
        // applied in the ES query builder). Sensitivity gating already
        // happens server-side via safety-score thresholds + the
        // `_finalArtworkFilter` post-filter, so we drop the param here.
      }}
      before={before}
      after={after}
      // Standard relay-spec cursor convention for all three endpoints:
      //   * next page = `first` + `after` = endCursor
      //   * prev page = `last`  + `before` = startCursor
      // The "physical" direction differs by orderBy — for /latest and
      // /most-liked (desc) "next" walks toward older / less-liked items;
      // for /oldest (asc) "next" walks toward newer items — but the
      // cursor wire-format is identical, which is what `isPosSeq=true`
      // gives us. (The legacy `isPosSeq=false` branch in
      // `BaseCursorPaging` inverts cursor direction and only works when
      // paired with Apollo's relay-style cache; on a stateless backend
      // it sends `first+after=endCursor` for "Prev", which the relay
      // handler correctly interprets as walking forward — i.e. older
      // items — so the user never gets back to page 1.)
      isPosSeq
      onCursorChange={onCursorChange}
      preferHashParams
    />
  )
}

function BookmarkedWorkList({
  isMe,
  bookmarkId,
  type,
  editing,
  selectedItems,
  pagingRef,
  collectionListRef,
  tapArtwork,
  tapModel,
  showContentSafetyLabel,
}: {
  isMe: boolean
  bookmarkId: string
  type?: WorkTypes | ''
  editing: boolean
  selectedItems: string[]
  showNsfw: boolean
  pagingRef: RefObject<BaseCursorPagingContextType | null> | undefined
  collectionListRef: RefObject<CollectionListRef | null>
  tapArtwork: (artwork: ArtworkPreviewFragment) => void
  tapModel: (model: GenerationModel) => void
  showContentSafetyLabel?: boolean
}) {
  const { t } = useTranslation(['artwork'])
  const [{ after: queryAfter, before: queryBefore }] = useQs()
  const [hash, setHash] = useHash()
  const hashParams = useMemo(
    () => new URLSearchParams(hash.replace(/^#/, '')),
    [hash],
  )

  const queryValues = useMemo(
    () => ({
      after: queryAfter,
      before: queryBefore,
    }),
    [queryAfter, queryBefore],
  )

  // Migrate from query params to hash params (one-time migration)
  useEffect(() => {
    const FILTER_KEYS = ['after', 'before'] as const
    const hasQueryParams = FILTER_KEYS.some(key => queryValues[key])
    if (!hasQueryParams) return

    const url = new URL(window.location.href)
    for (const key of FILTER_KEYS) {
      url.searchParams.delete(key)
    }

    const params = new URLSearchParams(url.hash.replace(/^#/, ''))
    for (const key of FILTER_KEYS) {
      const value = queryValues[key]
      if (value) {
        params.set(key, value)
      } else {
        params.delete(key)
      }
    }

    url.hash = params.toString()
    // Use replaceState instead of navigate() to avoid intermediate state where both query and hash params exist
    window.history.replaceState(null, '', url.toString())

    setHash(params.toString())
  }, [queryValues, setHash])

  const after = hashParams.get('after') || undefined
  const before = hashParams.get('before') || undefined

  const onCursorChange = (args: Record<string, any>) => {
    const params = new URLSearchParams(hash.replace(/^#/, ''))
    if (args.after) {
      params.set('after', args.after)
      params.delete('before')
    } else if (args.before) {
      params.set('before', args.before)
      params.delete('after')
    } else {
      params.delete('after')
      params.delete('before')
    }
    setHash(params.toString())
  }

  return (
    <Suspense fallback={<Preloader />}>
      <BaseCursorPaging
        useSWR={useListBookmarkItems}
        extraArgs={{
          bookmarkId: bookmarkId,
          orderBy: 'refId',
          ...match(type)
            .with('', undefined, () => ({
              types: [RefType.Artwork, RefType.Model],
            }))
            .with('artwork', () => ({
              types: [RefType.Artwork],
              artworkTypes: [ArtworkType.Default, ArtworkType.Album],
            }))
            // @deprecated at 2025-07-23 'manga'
            // .with('manga', () => ({
            //   types: [RefType.Artwork],
            //   artworkTypes: [ArtworkType.Comic, ArtworkType.AnimatedComic],
            // }))
            .with('animation', () => ({
              types: [RefType.Artwork],
              artworkTypes: [ArtworkType.AnimatedArtwork],
            }))
            .with('model', () => ({
              types: [RefType.Model],
            }))
            // @deprecated at 2025-07-23 'manga'
            .otherwise(() => null),
        }}
        getConnection={r => r}
        pagingRef={pagingRef}
        pageSize={20}
        before={before}
        after={after}
        onCursorChange={onCursorChange}
        preferHashParams
        renderItem={(bookmarkItem: BookmarkItemBaseFragment) =>
          match(bookmarkItem)
            .with({ artwork: P.select({}) }, artwork => (
              <BookmarkedWorkCard
                key={artwork.id}
                isMe={isMe}
                bookmarkId={bookmarkId}
                artwork={artwork}
                isEdit={editing}
                selected={selectedItems.includes(artwork.id)}
                tapArtwork={tapArtwork as never}
                showContentSafetyLabel={showContentSafetyLabel}
                collectionListRef={collectionListRef}
              />
            ))
            .with({ model: P.select({}) }, model => (
              <ModelCard
                key={model.id}
                model={model}
                link
                selected={
                  editing ? selectedItems.includes(model.id) : undefined
                }
                onClick={(_, model) => {
                  // TODO: Open model in modal viewer
                  tapModel(model)
                }}
              />
            ))
            .otherwise(() => null)
        }
        empty={<Empty description={t('artwork:no-content')} />}
      />
    </Suspense>
  )
}

const PickItemDialog: FC<{
  open: boolean
  userId: string
  loading?: boolean
  confirmSelected: (items: Array<{ id: string; type: string }>) => void
  onClose: () => any
}> = ({ open, userId, loading, confirmSelected, onClose }) => {
  return (
    <UIDialog
      open={open}
      onClose={onClose}
      className="max-w-screen-mmd"
      style={{
        width: 'calc(100vw - 2rem)',
      }}
    >
      <PickItemDialogContent
        {...{
          userId,
          loading,
          confirmSelected,
          onClose,
        }}
      />
    </UIDialog>
  )
}

function mapWorkTypeToItemType(workType: WorkTypes) {
  switch (workType) {
    case 'artwork':
      return [ArtworkType.Default, ArtworkType.Album]
    // @deprecated at 2025-07-23
    // case 'manga':
    //   return [ArtworkType.Comic]
    case 'animation':
      return [ArtworkType.AnimatedArtwork]
  }
}

const PickItemDialogContent: FC<{
  userId: string
  loading?: boolean
  confirmSelected: (items: Array<{ id: string; type: string }>) => any
  onClose: () => any
}> = ({ userId, loading, confirmSelected, onClose }) => {
  const [cursorState, setCursorState] = useState<{
    before?: string
    after?: string
  }>({})
  const [selectedItems, setSelectedItems] = useState<
    Array<{ id: string; type: string }>
  >([])
  const selectedIds = useMemo(
    () => selectedItems.map(it => it.id),
    [selectedItems],
  )

  const { t } = useTranslation(['artwork', 'common'])

  const [workType, setWorkType] = useState<WorkTypes>('artwork')

  const toggleSelected = (id: string, type: string) => {
    const newSelectedItems = selectedItems.some(it => it.id === id)
      ? selectedItems.filter(it => it.id !== id)
      : [...selectedItems, { id, type }]
    setSelectedItems(newSelectedItems)
  }

  return (
    <>
      <UIDialogHeader heading={t('artwork:choose-artwork')} />
      <UIDialogContent className="flex flex-col">
        <WorkTypeTabs
          accept={['artwork', 'model', 'animation']} // @deprecated at 2025-07-23 'manga'
          value={workType}
          onChange={value => {
            const next = value || 'artwork'
            if (next !== workType) setCursorState({})
            setWorkType(next)
          }}
          className="mb-4"
        />
        {match(workType)
          .with('artwork', 'animation', workType => (
            // @deprecated at 2025-07-23 'manga'
            <AllArtworksList
              userId={userId}
              isMe={true}
              types={mapWorkTypeToItemType(workType)}
              editing={true}
              selectedItems={selectedIds}
              pagingRef={undefined}
              tapArtwork={(artwork: ArtworkPreviewFragment) => {
                toggleSelected(artwork.id, 'artwork')
              }}
              cursorState={cursorState}
              onCursorChange={setCursorState}
            />
          ))
          .with('model', () => (
            <UserModelList
              userId={userId}
              editing
              selectedItems={selectedIds}
              onClickModel={model => toggleSelected(model.id, 'model')}
              cursorState={cursorState}
              onCursorChange={setCursorState}
            />
          ))
          .otherwise(() => null)}
      </UIDialogContent>
      <UIDialogFooter>
        <span className="self-center mr-auto">
          {selectedItems.length} {'selected'}
        </span>

        <UIButton onPress={onClose} variant="outlined" className="capitalize">
          {t('action:cancel')}
        </UIButton>

        <UIButton
          busy={loading}
          onPress={() => {
            confirmSelected(selectedItems)
            track('collection_image_add_confirm')
          }}
          variant="filled"
        >
          {t('action:confirm')}
        </UIButton>
      </UIDialogFooter>
    </>
  )
}

function BookmarkedWorkCard({
  bookmarkId,
  isMe,
  isEdit,
  selected,
  artwork,
  tapArtwork,
  showContentSafetyLabel,
  collectionListRef,
}: {
  bookmarkId: string
  isMe: boolean
  isEdit: boolean
  selected: boolean
  artwork: ArtworkPreviewFragment
  tapArtwork: (artwork: ArtworkPreviewFragment) => void
  showContentSafetyLabel?: boolean
  collectionListRef: RefObject<CollectionListRef | null>
}) {
  return (
    <div className="relative">
      <ArtworkPreviewCardSimple
        key={artwork.id}
        artwork={artwork}
        to={getArtworkLink(artwork)}
        isEdit={isEdit}
        selected={selected}
        onClick={ev => ev.preventDefault()}
        onArtworkClick={tapArtwork as never}
        contentSafetyLabelType={
          showContentSafetyLabel
            ? getContentSafetyLabelForUserContent(artwork)
            : undefined
        }
        trackingArgs={{
          scene: 'collection',
          feed: OrderOption.newest,
        }}
      />
      {isMe && !isEdit && (
        <div className="absolute top-0 right-0 overflow-visible size-0">
          <BookmarkedWorkCardMenu
            collectionListRef={collectionListRef}
            artwork={artwork}
            bookmarkId={bookmarkId}
          />
        </div>
      )}
    </div>
  )
}

type BookmarkedWorkCardMenuItemProps = StyleProps & {
  key: string
  label: ReactNode
  onClick?: (artwork: ArtworkPreviewFragment) => void
}

function BookmarkedWorkCardMenu(props: {
  bookmarkId: string
  artwork: ArtworkPreviewFragment
  collectionListRef: RefObject<CollectionListRef | null>
  menu?: Array<BookmarkedWorkCardMenuItemProps | Falsy>
}) {
  const { t } = useTranslation(['artwork'])
  const resolvedMenu: BookmarkedWorkCardMenuItemProps[] = compact(
    [
      [
        {
          key: 'set-cover',
          label: t('artwork:action.use-as-collection-cover'),
          onClick: async () => {
            try {
              await updateCollection(props.bookmarkId, {
                coverMediaId: props.artwork.mediaId,
              })
              props.collectionListRef.current?.reload(true)

              toast.success(t('user:collection.notice.update.success'))
            } catch {
              toast.error(t('user:collection.notice.update.failed'))
            }
          },
        },
      ],
      ...(props.menu ?? []),
    ].flat(),
  )

  if (resolvedMenu.length === 0) return null

  return (
    <UIMenuTrigger>
      <AriaButton className="absolute top-1.5 right-1.5 flex justify-center items-center size-7 text-white bg-black/50 ring-1 ring-white/30 backdrop-blur rounded-full">
        <MingcuteMore2Fill className="size-5" />
      </AriaButton>
      <UIMenu placement="bottom end">
        {resolvedMenu.map(item => (
          <UIMenuItem
            key={item.key}
            className={item.className}
            style={item.style}
            onAction={() => item.onClick?.(props.artwork)}
          >
            {item.label}
          </UIMenuItem>
        ))}
      </UIMenu>
    </UIMenuTrigger>
  )
}
